import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses';
import { AddOnVisibilityOperationType } from '../../common/enums';
import {
  addMsg,
  additionalServicesMsg,
  continueShoppingMsg,
  quantityMsg,
  removeMsg,
  subtractMsg,
  t,
} from '../../common/i18n';
import { clampQuantity } from '../../common/utils/quantityUtils';
import { useAuth } from '../../public/site/AuthProvider';
import { DialogWrapper } from '../DialogWrapper';
import { AdditionalServicesCheckoutContent } from '../OrderSubscriptionConfiguration/AdditionalServicesCheckoutContent';
import { ScrollHintButton } from '../ScrollHintButton/ScrollHintButton';
import { ProductVariantSelection } from './ProductVariantSelection';
import { Price, getSaving, isMobileView } from './SubscriptionUtils';
import type { OrderProps, PublicPageOrderProps, SubscriptionCardWithModel } from './types';
import type { AddedAddon } from '../../common/types/addon';
import type { ChildOrderItem } from '../../generated/api/childOrderItem';
import type { CommercialProduct } from '../../generated/api/models';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { SubscriptionCardOffer } from '../SubscriptionCard/SubscriptionCard';
import type { JSX } from 'react';

import './SubscriptionModal.scss';

export interface ModalDescriptionItem {
  title?: string;
  content?: JSX.Element;
}

export interface SubscriptionCard2ModalProps {
  header: string;
  descriptionItems: ModalDescriptionItem[];
  onNumberSelectionChange?: (newOrKeepNumber: string) => void;
  onCloseDialog: () => void;
  onOrder: (p: PublicPageOrderProps | OrderProps, addons?: ChildOrderItem[]) => void;
  onlineModel: OnlineModel;
  subscriptionCard?: SubscriptionCardWithModel;
  newOrKeepNumber?: string;
  offers?: SubscriptionCardOffer[];
  hasQuantity?: boolean;
  pricePreamble?: string;
  isChangeOffer?: boolean;
  showAlternateProductChoice?: boolean;
  onSelectOffer?: (guid: string, name: string) => void;
  showAddonConfiguration?: boolean;
  commercialProduct?: CommercialProduct;
  stickyFooter?: boolean;
  showNewOrKeepNumber?: boolean;
}

export const SubscriptionModal = ({
  header,
  descriptionItems,
  onNumberSelectionChange,
  onCloseDialog,
  onOrder,
  onlineModel,
  subscriptionCard,
  newOrKeepNumber,
  offers,
  hasQuantity,
  pricePreamble,
  isChangeOffer,
  showAlternateProductChoice = false,
  onSelectOffer,
  showAddonConfiguration = false,
  commercialProduct,
  stickyFooter = false,
  showNewOrKeepNumber = true,
}: SubscriptionCard2ModalProps) => {
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState<number>(1);
  const modalMainRef = useRef<HTMLDivElement>(null);
  const addonsRef = useRef<HTMLDivElement>(null);
  const hasOfferChoices =
    subscriptionCard && 'offers' in subscriptionCard && (subscriptionCard.offers?.length ?? 0) > 1;

  const includedAddons: AddedAddon[] =
    subscriptionCard?.services
      ?.filter(s => s.isIncluded && s.guid !== undefined)
      .filter(s => !s.applicableOfferGuid || s.applicableOfferGuid === subscriptionCard.selectedOffer)
      .map(s => ({
        addOnCode: s.guid as string,
        displayName: '',
        addOnAssociationCode: '',
        display: true,
      })) ?? [];

  const [selectedAddons, setSelectedAddons] = useState<AddedAddon[]>([]);
  const includedAddOnCodes = includedAddons.map(addon => addon.addOnCode);

  useEffect(() => {
    setSelectedAddons([]);
  }, [subscriptionCard?.selectedOffer]);

  const methods = useForm({ defaultValues: {} });
  const cartItemInstanceId = commercialProduct ? `modal-${commercialProduct.commercialProductCode}` : 'modal';

  const handleAddOnSelectionChange = (_cartIndex: string, addedAddOns: AddedAddon[]) => {
    setSelectedAddons(addedAddOns);
  };

  const totalAddonsMonthlyPrice = selectedAddons.reduce((sum, addon) => sum + (addon.monthlyPrice ?? 0), 0);
  const totalAddonsOneTimePrice = selectedAddons.reduce((sum, addon) => sum + (addon.oneTimePrice ?? 0), 0);

  const displayOneTimePrice = (() => {
    if (isChangeOffer) {
      return subscriptionCard?.serviceFee;
    }
    if (showAddonConfiguration) {
      return (subscriptionCard?.oneTimePrice ?? 0) + totalAddonsOneTimePrice;
    }
    return subscriptionCard?.oneTimePrice;
  })();

  const displayMonthlyPrice = showAddonConfiguration
    ? (subscriptionCard?.monthlyPrice ?? 0) + totalAddonsMonthlyPrice
    : subscriptionCard?.monthlyPrice;

  const buttonText = (() => {
    if (!isChangeOffer) {
      return t.B5J1('Add to basket');
    }
    return t.I62A('Continue');
  })();

  return (
    <DialogWrapper size="l" closeable={true} onCloseDialog={onCloseDialog} header={header}>
      <FormProvider {...methods}>
        <div
          className={classNames('of-subscription-modal', {
            ['of-subscription-modal--sticky-footer']: stickyFooter,
          })}
        >
          <div className="of-subscription-modal-main" ref={modalMainRef}>
            <div className="of-subscription-modal-body">
              {showNewOrKeepNumber && !isChangeOffer && !showAddonConfiguration && onNumberSelectionChange && (
                <div className="of-subscription-modal-number-select">
                  <h4 className={dsClass.MARGIN_0}>{t.X357('Select new or current phone number ')}</h4>
                  <CL.RadioGroup onChange={onNumberSelectionChange} value={newOrKeepNumber}>
                    <CL.RadioV2 value="newNumber">
                      <div>
                        <span>{t.ARFV('New number')} </span>
                        {t.ARFX('- select this option if you want your subscription to have a new number.')}
                      </div>
                    </CL.RadioV2>
                    <CL.RadioV2 value="keepNumber">
                      <div>
                        <span>{t.ARFW('Keep your number')} </span>
                        {t.ARFY(
                          '- select this option if you want to transfer your current number from another operator to Elisa.'
                        )}
                      </div>
                    </CL.RadioV2>
                  </CL.RadioGroup>
                </div>
              )}
              <div className="of-subscription-modal-details-description">
                <CL.Description
                  listTitle={t.NUCX('Subscription contents')}
                  items={descriptionItems.map(item => ({
                    title: item.title,
                    description: item.content,
                  }))}
                />
              </div>
              {showAlternateProductChoice && hasOfferChoices && (
                <div className={classNames(dsClass.MARGIN_TOP_4, 'of-subscription-modal-product-variant-container')}>
                  <ProductVariantSelection subscriptionCard={subscriptionCard} onSelectOffer={onSelectOffer} />
                </div>
              )}
              {showAddonConfiguration && commercialProduct && (
                <div ref={addonsRef} className="of-subscription-modal-addons">
                  <AdditionalServicesCheckoutContent
                    key={subscriptionCard?.selectedOffer}
                    commercialProduct={commercialProduct}
                    cartItemInstanceId={cartItemInstanceId}
                    productAddOns={selectedAddons}
                    includedAddOnCodes={includedAddOnCodes}
                    onAddOnSelectionChange={handleAddOnSelectionChange}
                    hideHeader={false}
                    addOnVisibilityOperationType={
                      isChangeOffer ? AddOnVisibilityOperationType.CHANGE : AddOnVisibilityOperationType.NEW_SALES
                    }
                    hideDefaultAddons={true}
                  />
                </div>
              )}
            </div>
            {isMobileView && showAddonConfiguration && (
              <ScrollHintButton
                targetRef={addonsRef}
                containerRef={modalMainRef}
                ariaLabel={t.LXSR(additionalServicesMsg)}
              />
            )}
          </div>
          <div className="of-subscription-modal-footer ds-border--top-style-solid">
            <div className="of-subscription-modal-footer-content">
              <Price
                onetime={displayOneTimePrice}
                monthly={displayMonthlyPrice}
                inModal={true}
                quantity={quantity}
                pricePreamble={pricePreamble}
                saving={showAddonConfiguration ? undefined : getSaving(subscriptionCard, quantity)}
                isChangeOffer={isChangeOffer}
              />
              <div className="of-subscription-modal-footer-buttons">
                <div className="of-subscription-modal-footer-buttons-quantity">
                  <div
                    className={`of-subscription-modal-footer-buttons-quantity-label ${dsClass.MARGIN_BOTTOM_1} ${dsClass.MARGIN_TOP_3} ${dsClass.HIDE_M_AND_UP}`}
                  >
                    <span className={`${dsClass.TEXT_L} ${dsClass.FONT_WEIGHT_700}`}>{t.M0W7(quantityMsg)}</span>
                  </div>
                  {hasQuantity && (
                    <CL.Quantity
                      className={`of-subscription-quantity ${dsClass.MARGIN_RIGHT_2}`}
                      i18n_quantity_deleteAriaLabel={t.R3VE(removeMsg)}
                      i18n_quantity_minusAriaLabel={t.C2KQ(subtractMsg)}
                      i18n_quantity_plusAriaLabel={t.VKFM(addMsg)}
                      i18n_quantity_quantityAriaLabel={t.M0W7(quantityMsg)}
                      minValue={1}
                      currentValue={1}
                      onChange={(q: number) => setQuantity(clampQuantity(q))}
                    />
                  )}
                </div>
                <div className="of-subscription-modal-footer-buttons-add-to-cart">
                  <CL.Button onClick={() => onCloseDialog()} color="link" className={dsClass.HIDE_M_AND_UP}>
                    {t.VLZR(continueShoppingMsg)}
                  </CL.Button>
                  <CL.Button
                    onClick={() => {
                      if (!isChangeOffer) {
                        onCloseDialog();
                      }

                      const childOrderItems: ChildOrderItem[] = showAddonConfiguration
                        ? [...includedAddons, ...selectedAddons].map(addon => ({
                            addOnCode: addon.addOnCode,
                            addOnAssociationCode: addon.addOnAssociationCode,
                            addOnAttributes: addon.addOnAttributes,
                          }))
                        : [];

                      onOrder(
                        {
                          isAuthenticated: isAuthenticated,
                          onlineModel: onlineModel,
                          quantity: quantity,
                          isNewNumber: newOrKeepNumber === 'newNumber',
                          offers: offers || [],
                          offerGuid: subscriptionCard?.selectedOffer,
                          voucherCode: subscriptionCard?.voucherCode,
                          services: subscriptionCard?.services,
                        },
                        childOrderItems.length > 0 ? childOrderItems : undefined
                      );
                    }}
                    size="l"
                  >
                    {buttonText}
                  </CL.Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </DialogWrapper>
  );
};
